/**
 * MediaTypeClassifier — filename → what kind of media it is.
 * Pure module: no imports, no I/O.
 */

import type { MediaType } from '../types';

const IMAGE_EXT = new Set(['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'tif', 'tiff', 'heic', 'heif']);
const VIDEO_EXT = new Set(['mp4', 'mov', 'webm', 'mkv', 'avi', 'm4v']);

/** Extensions the browser can decode natively today. RAW lands in phase 2. */
const BROWSER_DECODABLE = new Set(['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp']);

export function extensionOf(filename: string): string {
  const dot = filename.lastIndexOf('.');
  if (dot <= 0 || dot === filename.length - 1) return '';
  return filename.slice(dot + 1).toLowerCase();
}

/** Null means "not media we handle" — the scanner skips these silently. */
export function classify(filename: string): MediaType | null {
  const ext = extensionOf(filename);
  if (IMAGE_EXT.has(ext)) return 'image';
  if (VIDEO_EXT.has(ext)) return 'video';
  return null;
}

/** True when the browser can turn this file into a bitmap without a WASM decoder. */
export function isBrowserDecodable(filename: string): boolean {
  return BROWSER_DECODABLE.has(extensionOf(filename));
}

/** Hidden files and macOS resource forks — never worth scanning. */
export function isIgnoredName(name: string): boolean {
  return name.startsWith('.') || name.startsWith('._') || name === 'Thumbs.db';
}
